import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { format } from "date-fns"
import { Calendar, MapPin, Clock, Users } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { EventRegistrationForm } from "@/components/events/EventRegistrationForm"
import { supabase } from "@/integrations/supabase/client"
import { Event } from "@/types/events"
import { Skeleton } from "@/components/ui/skeleton"

export default function PublicEventPage() {
  const { slug } = useParams()
  const [event, setEvent] = useState<Event | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const { data, error } = await supabase
          .from("events")
          .select(`
            *,
            event_participants!event_participants_event_id_fkey (count),
            corporate_profiles (company_name, subdomain),
            corporate_contact:corporate_contacts (
              id,
              corporate_id,
              name,
              email,
              contact,
              designation,
              profile_picture_url,
              created_at,
              updated_at
            )
          `)
          .eq("slug", slug)
          .maybeSingle()

        if (error) throw error
        setEvent(data as Event)
      } catch (err: any) {
        console.error("Error fetching event:", err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    if (slug) {
      fetchEvent()
    }
  }, [slug])

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-12 w-3/4 mb-4" />
        <Skeleton className="h-64 w-full mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-red-600">
          {error || "Event not found"}
        </h1>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-6">{event.event_name}</h1>

        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center text-gray-600">
                <Calendar className="h-5 w-5 mr-2" />
                <span>{format(new Date(event.event_date), "PPP")}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Clock className="h-5 w-5 mr-2" />
                <span>{event.event_time}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <MapPin className="h-5 w-5 mr-2" />
                <span>{event.location}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Users className="h-5 w-5 mr-2" />
                <span>{event.event_participants?.[0]?.count || 0} participants</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {event.description && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>About this Event</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{event.description}</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Register for this Event</CardTitle>
          </CardHeader>
          <CardContent>
            <EventRegistrationForm eventId={event.id} />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}